/**
 * R4.2 (Konzept § 4.7) — „Google meldet: dauerhaft geschlossen", am Entscheidungsort.
 *
 * WARUM HIER und nicht im Kunden-Profil oder im Anfragen-Drawer:
 *  - Der **Angebots-Builder** ist die Stelle, an der ein Mensch über ein Angebot entscheidet und
 *    es hinausschickt. „Vor dem Angebot" heißt genau hier. Im Kunden-Profil stünde derselbe
 *    Hinweis in einer Datenansicht, die man beim Angebotschreiben gar nicht offen hat (dort steht
 *    `business_status` seit jeher als roher Aufzählungswert in der Karte — informativ, aber kein
 *    Signal).
 *  - Der **Anfragen-Drawer** wäre noch früher, kann das Signal aber nicht führen: `inquiries` hat
 *    keine `customer_id` (packages/db/src/schema.ts), und die Places-Zeile hängt an genau der
 *    (`unique(tenant_id, customer_id)`, sql/93). Ein Signal dort bräuchte erst eine Verknüpfung,
 *    die es nicht gibt — das wäre ein zweites Kriterium, kein Nebenbei.
 *
 * Ein HINWEIS, keine Sperre: der Text ist beschreibend („Google meldet …") und nennt Quelle und
 * Stand, weil Google-Einträge auch von Dritten gemeldet werden und falsch sein können. Nichts
 * hier deaktiviert einen Knopf.
 *
 * Die Komponente rendert nur — ob und was dasteht, entscheidet `betriebsstatusWarnung()` aus
 * `@maestro/places-anzeige` (geprüft in `apps/api/test/places-mehrdeutig.test.ts`).
 */
import { betriebsstatusWarnung } from "@maestro/places-anzeige";
import { Icon } from "./components";
import { GoogleAttribution } from "./GoogleAttribution";
import type { GooglePlacesLookup } from "./types";

export function BetriebGeschlossenHinweis({ lookup, firma }: { lookup: GooglePlacesLookup | null; firma: string | null }) {
  const warnung = betriebsstatusWarnung({
    businessStatus: lookup?.businessStatus ?? null,
    lookedUpAt: lookup?.lookedUpAt ?? null,
    firma,
  });
  if (!warnung) return null;
  return (
    <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-3">
      <p className="flex items-center gap-2 font-label-md text-label-md text-secondary dark:text-[#b8ae9e]">
        <Icon name="warning" className="text-[18px]" />
        {warnung.satz}
      </p>
      <p className="mt-1 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">{warnung.zusatz}</p>
      <p className="mt-1 flex flex-wrap items-baseline gap-x-2 font-body-sm text-[11px] text-on-surface-variant/70">
        <span>{warnung.herkunft}</span>
        {/* R4.5 / § 6.2: Wo Google-Angaben stehen, steht Google dran — auch hier. */}
        <GoogleAttribution werte={warnung.googleWerte} />
      </p>
    </div>
  );
}
